<template>
  <div class="risk-evaluation-slot">
    <div v-if="loading" class="d-flex w-100 h-100 align-items-center justify-content-center">
      <b-spinner label="Loading" variant="primary" />
    </div>
    <div v-else class="w-100 h-100 overflow-auto p-1">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <h5 class="mb-0 text-capitalize">{{ $tc('risk.evaluation.title', 1) }}</h5>
        <b-badge pill variant="light-primary" v-if="currentState">
          {{ $t('custom_forms.state') || 'Estado' }}: {{ labelForState(currentState.toLowerCase()) }}
        </b-badge>
      </div>
      <!-- Inline header + history -->
      <div v-if="currentEvaluationId" class="mb-1">
        <div class="d-flex flex-column" style="gap: 12px;">
          <div class="info-card">
            <div class="info-row">
              <span class="info-label">{{ tf('risk.reference_number', 'Número de Referencia') }}:</span>
              <span class="info-value">{{ referenceNumber }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ tc('unit.title', 1, 'Instalación') }}:</span>
              <span class="info-value">{{ unitNameDisplay }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">REMS:</span>
              <span class="info-value">{{ rems }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">SIRH:</span>
              <span class="info-value">{{ sirh }}</span>
            </div>
            <div v-if="templateNames" class="info-row">
              <span class="info-label">{{ tf('risk.template.title', 'Plantilla') }}:</span>
              <span class="info-value">{{ templateNames }}</span>
            </div>
          </div>
          <div class="history-card">
            <div class="history-header d-flex align-items-center justify-content-between">
              <strong>{{ tf('custom_forms.history', 'Historial') }}</strong>
              <small class="text-muted">{{ sortedHistory.length }} {{ tf('custom_forms.entries', 'entradas') }}</small>
            </div>
            <div class="history-table" v-if="sortedHistory.length">
              <div class="history-row history-row--head" :style="historyGridStyle">
                <div class="col-state">{{ tf('custom_forms.state', 'Estado') }}</div>
                <div class="col-user">{{ tf('custom_forms.user', 'Usuario') }}</div>
                <div class="col-date">{{ tf('custom_forms.date', 'Fecha') }}</div>
                <div v-if="hasHistoryComments" class="col-note">
                  {{ tf('custom_forms.note', 'Nota') }}
                </div>
              </div>
              <div
                v-for="s in visibleHistory"
                :key="s.id"
                class="history-row"
                :style="historyGridStyle"
              >
                <div class="col-state">
                  <b-badge pill :variant="badgeFor(s.name)" class="history-badge">
                    {{ labelForState(s.name) }}
                  </b-badge>
                </div>
                <div class="col-user">
                  <div class="history-user" :title="formatUser(s)">
                    <div class="user-name">{{ getUserName(s) || '—' }}</div>
                    <div v-if="getUserEmail(s)" class="user-email">
                      {{ getUserEmail(s) }}
                    </div>
                  </div>
                </div>
                <div class="col-date">
                  <small class="history-date">{{ formatDate(s.created_at) }}</small>
                </div>
                <div v-if="hasHistoryComments" class="col-note">
                  <small class="text-muted" :title="getStatusComment(s)">{{ getStatusComment(s) || '—' }}</small>
                </div>
              </div>
            </div>
            <div v-else class="text-muted small py-1">
              {{ tf('custom_forms.empty', 'Sin movimientos') }}
            </div>
            <div v-if="sortedHistory.length > limitHistory" class="text-right mt-1">
              <b-button
                variant="link"
                size="sm"
                class="p-0"
                @click="showAllHistory = !showAllHistory"
              >
                {{
                  showAllHistory
                    ? tf('custom_forms.show_less', 'Ver menos')
                    : tf('custom_forms.show_all', 'Ver todo el historial')
                }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <!-- Inline form -->
      <build-risk-form
        v-if="currentEvaluationId && readyToRenderForm"
        :key="runnerKey"
        ref="runner"
        :risk-evaluation-id="currentEvaluationId"
        :template-snapshot="inlineSnapshot"
        :template-source-ids="inlineSources"
        :current-state="currentState"
        :is-reviewer="isReviewer"
        :can-edit="canEditForm"
        @submit="onAnswersSubmit"
      />
      <!-- Threats part -->
      <risk-threat-part
        v-if="currentEvaluationId && readyToRenderForm"
        ref="threats"
        :risk-evaluation-id="currentEvaluationId"
        :defer-save="true"
        :allow-partial="true"
        :skip-weight-validation="true"
        :autosave-mode="true"
        :can-edit="canEditForm"
        @changed="onChildDirty('threats:changed')"
        @dirty="onChildDirty('threats:dirty')"
        @input="onChildDirty('threats:input')"
        @weights-changed="onChildDirty('threats:weights')"
        @answers-changed="onChildDirty('threats:answers')"
      />
      <!-- NO/NA flagged answers part -->
      <risk-flagged-answers-part
        v-if="currentEvaluationId && readyToRenderForm"
        ref="flagged"
        :risk-evaluation-id="currentEvaluationId"
        :defer-save="true"
        :autosave-mode="true"
        @changed="onChildDirty('flagged:changed')"
        @dirty="onChildDirty('flagged:dirty')"
        @input="onChildDirty('flagged:input')"
        @followups-changed="onChildDirty('flagged:followups')"
      />
      <!-- Final part (review + executive) -->
      <div class="rfm-anchor rfm-aid-sec-final"></div>
      <risk-evaluation-final-part
        v-if="currentEvaluationId && readyToRenderForm"
        v-model="evaluationFinal"
        :disabled="saving"
        :can-edit="canEditForm"
        class="mt-1"
      />
      <!-- Reviewer actions -->
      <div v-if="currentEvaluationId" class="mt-1">
        <div class="text-center mb-50">
          <b-button v-if="showSaveButton" :disabled="saving" variant="primary" class="mb-1" @click="onClickSave">
            {{ $t('actions.save') || 'Guardar' }}
          </b-button>
        </div>
        <div v-if="canSendToReview && !(currentState === 'in_review' && isReviewer && canShowReassign)" class="d-flex flex-column flex-md-row justify-content-center align-items-center text-center" style="gap:8px;">
          <div class="mx-auto" style="min-width:220px;max-width:380px;flex:1;">
            <v-select
              v-model="selectedReviewer"
              :options="userOptions"
              :reduce="u => u.value"
              :clearable="false"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :placeholder="$t('custom_forms.select_reviewer') || 'Selecciona usuario revisor'"
            />
          </div>
          <b-button class="flex-grow-0 mx-auto" variant="warning" :disabled="!selectedReviewer || saving" @click="updateEvaluationStatus('in_review', selectedReviewer)">
            {{ $t('custom_forms.send_to_review') || 'Enviar a revisión' }}
          </b-button>
        </div>
        <template v-if="currentState === 'in_review' && isReviewer">
          <div class="d-flex flex-column flex-md-row mt-1 justify-content-center align-items-center text-center" style="gap:8px;">
            <b-button v-if="showApproveButtons" variant="success" @click="updateEvaluationStatus('approved')">{{ $t('actions.approve') }}</b-button>
            <b-button v-if="showApproveButtons" variant="danger" @click="updateEvaluationStatus('rejected')">{{ $t('actions.reject') }}</b-button>
            <template v-if="canShowReassign">
              <div class="reviewer-inline mr-2">
                <v-select
                  v-model="selectedReviewer"
                  :options="userOptions"
                  :reduce="u => u.value"
                  :clearable="false"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="$t('custom_forms.select_reviewer') || 'Selecciona usuario revisor'"
                  input-id="reviewer-reassign-inline"
                  class="select-opens-up"
                />
              </div>
              <b-button variant="warning" :disabled="!selectedReviewer || saving" @click="updateEvaluationStatus('in_review', selectedReviewer)">
                {{ $t('custom_forms.send_to_review') || 'Reasignar' }}
              </b-button>
            </template>
          </div>
        </template>
        <template v-else-if="canReassignAfterRejected">
          <div class="d-flex flex-column flex-md-row mt-1 justify-content-center align-items-center text-center" style="gap:8px;">
            <div class="reviewer-inline mr-2">
              <v-select
                v-model="selectedReassign"
                :options="reassignableOptions"
                :reduce="u => u.value"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :placeholder="$t('custom_forms.select_reassigner') || 'Selecciona usuario para reasignar'"
                input-id="reassign-after-reject"
                class="select-opens-up"
              />
            </div>
            <b-button variant="warning" :disabled="!selectedReassign || saving" @click="updateEvaluationStatus('reassigned', selectedReassign)">
              {{ $t('custom_forms.reassign') || 'Reasignar' }}
            </b-button>
          </div>
        </template>
      </div>
      <!-- Wizard trigger -->
      <risk-evaluation-wizard @open-evaluation-modal="handleWizardOpen" />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, getCurrentInstance, watch, onBeforeUnmount } from '@vue/composition-api'
import axios from '@/libs/axios'
import RiskFormAnswers from '@/components/BuildRiskForm/RiskFormAnswers.vue'
import RiskFormNormalizer from '@/components/BuildRiskForm/RiskFormNormalizer'
import FileUploadWithPreview from '@/components/BuildRiskForm/FileUploadWithPreview.vue'
import RiskEvaluationWizard from '@/views/risk/evaluations/RiskEvaluationWizard.vue'
import BuildRiskForm from '@/components/BuildRiskForm'
import RiskThreatPart from '@/components/RiskThreatPart.vue'
import RiskFlaggedAnswersPart from '@/components/RiskFlaggedAnswersPart.vue'
import RiskEvaluationFinalPart from '@/components/RiskEvaluationFinalPart.vue'
import vSelect from 'vue-select'
import { BButton, BBadge, BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'RiskEvaluationSlot',
  components: { RiskFormAnswers, RiskEvaluationWizard, BuildRiskForm, RiskThreatPart, RiskFlaggedAnswersPart, RiskEvaluationFinalPart, vSelect, BButton, BBadge, BSpinner },
  props: {
    data: { type: Object, required: true },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy
    const loading = ref(true)
    const saving = ref(false)
    const evaluation = ref(null)
    const currentEvaluationId = ref(null)
    const inlineSnapshot = ref(null)
    const inlineSources = ref(null)
    const referenceNumber = ref('')
    const unitNameDisplay = ref('')
    const rems = ref('')
    const sirh = ref('')
    const currentState = ref('in_progress')
    const templateNames = ref('')
    const statuses = ref([])
    const readyToRenderForm = ref(false)
    const runnerKey = ref(0)
    const showAllHistory = ref(false)
    const limitHistory = ref(6)
    const selectedReviewer = ref(null)
    const selectedReassign = ref(null)
    const lastStatusUserId = ref(null)
    const ownerUserId = ref(null)
    const evaluationFinal = ref({ flagged_comments: '', grade_reviwed_residual_risk: null, executive_resume: '' })
    const didBootstrap = ref(false)
    const creating = ref(false)
    const userOptions = ref([])
    const reassignableOptions = ref([])
    const approvePermNeeded = ref(null)
    const inherentLevel = ref(null)
    const asEnabled = ref(true)
    const asDebounceMs = ref(3000)
    const asMaxIntervalMs = ref(20000)
    const asDebounce = ref(null)
    const asMax = ref(null)
    const asBusy = ref(false)
    const asQueued = ref(false)

    const getLocalUserId = () => {
      try {
        const raw = localStorage.getItem('user') || localStorage.getItem('userData') || ''
        if (!raw) return null
        const obj = JSON.parse(raw)
        const candidates = [obj?.id, obj?.user?.id, obj?.data?.id, obj?.user_id, obj?.uid]
        for (const c of candidates) {
          const n = Number(c)
          if (Number.isFinite(n) && n > 0) return n
        }
        return null
      } catch {
        return null
      }
    }

    const getLocalUserEmail = () => {
      try {
        const raw = localStorage.getItem('user') || localStorage.getItem('userData') || ''
        if (!raw) return null
        const obj = JSON.parse(raw)
        const candidates = [obj?.email, obj?.user?.email, obj?.data?.email]
        for (const c of candidates) {
          if (c && typeof c === 'string' && c.includes('@')) return c
        }
        return null
      } catch {
        return null
      }
    }

    const isReviewer = computed(() => {
      try {
        if (!(vm.$can && vm.$can('review_risk_evaluation', 'RiskEvaluation'))) return false
      } catch (_) {
        return false
      }
      const st = String(currentState.value || '').toLowerCase()
      if (st !== 'in_review') return false
      let assignedId = lastStatusUserId.value
      let assignedEmail = null
      const list = Array.isArray(sortedHistory.value) ? sortedHistory.value : []
      const lastReview = list.find(s => String(s?.name || '').toLowerCase() === 'in_review')
      if (lastReview) {
        assignedId = assignedId || lastReview?.id_user || lastReview?.user_id || lastReview?.reviewer_id || lastReview?.id_reviewer || lastReview?.user?.id || lastReview?.userId || null
        assignedEmail = lastReview?.user?.email || lastReview?.email || null
      }
      const currentUserId = getLocalUserId()
      const currentUserEmail = getLocalUserEmail()
      const idMatch = Number(assignedId) > 0 && Number(currentUserId) > 0 && Number(assignedId) === Number(currentUserId)
      const emailMatch = assignedEmail && currentUserEmail && String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase()
      return !!(idMatch || emailMatch)
    })

    const canEditForm = computed(() => {
      const st = String(currentState.value || '').toLowerCase()
      const currentUserId = getLocalUserId()
      const currentUserEmail = getLocalUserEmail()

      if (!currentUserId && !currentUserEmail) return false

      if (['approved', 'rejected', 'canceled', 'cancelled'].includes(st)) return false

      if (st === 'in_review') {
        return false
      }

      if (st === 'in_progress' || st === 'reassigned') {
        const ownerId = ownerUserId.value ? Number(ownerUserId.value) : null
        if (ownerId && currentUserId && Number(ownerId) === Number(currentUserId)) {
          return true
        }
        return false
      }

      return false
    })

    const hasSpecialReasingForRisk = computed(() => {
      try {
        return !!(vm.$can && vm.$can('special_reasing', 'RiskEvaluation'))
      } catch (_) {
        return false
      }
    })

    const canSendToReview = computed(() => {
      const st = String(currentState.value || '').toLowerCase()
      const currentUserId = getLocalUserId()
      const currentUserEmail = getLocalUserEmail()
      const finalStates = ['approved', 'rejected', 'canceled', 'cancelled']
      if (finalStates.includes(st)) return false

      if (!currentUserId && !currentUserEmail) {
        return hasSpecialReasingForRisk.value
      }

      if (st === 'in_review') {
        return hasSpecialReasingForRisk.value
      }

      if (st === 'in_progress' || st === 'reassigned') {
        const ownerId = ownerUserId.value ? Number(ownerUserId.value) : null
        if (ownerId && currentUserId && Number(ownerId) === Number(currentUserId)) {
          return true
        }
        return hasSpecialReasingForRisk.value
      }

      return hasSpecialReasingForRisk.value
    })

    const canReassignAfterRejected = computed(() => {
      try {
        const st = String(currentState.value || '').toLowerCase()
        if (st !== 'rejected') return false
        return !!(vm.$can('review_risk_evaluation', 'RiskEvaluation') || vm.$can('approve_low_risk', 'RiskEvaluation') || vm.$can('approve_high_risk', 'RiskEvaluation'))
      } catch (_) {
        return false
      }
    })

    const canShowReassign = computed(() => {
      const st = String(currentState.value || '').toLowerCase()
      return st !== 'approved' && st !== 'rejected'
    })

    const sortedHistory = computed(() => {
      const arr = Array.isArray(statuses.value) ? statuses.value.slice() : []
      return arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    })

    const visibleHistory = computed(() => {
      const base = Array.isArray(sortedHistory.value) ? sortedHistory.value : []
      return showAllHistory.value ? base : base.slice(0, limitHistory.value)
    })

    const hasHistoryComments = computed(() => {
      return (Array.isArray(statuses.value) ? statuses.value : []).some(s => !!getStatusComment(s))
    })

    const showSaveButton = computed(() => {
      const state = String(currentState.value || '').toLowerCase()
      if (['approved', 'rejected', 'canceled', 'cancelled'].includes(state)) return false
      return canEditForm.value
    })

    const showApproveButtons = computed(() => {
      if (!(currentState.value === 'in_review' && isReviewer.value)) return false
      const p = approvePermNeeded.value
      try {
        if (!vm.$can) return false
        if (p === 'approve_low_risk') return !!vm.$can('approve_low_risk', 'RiskEvaluation')
        if (p === 'approve_high_risk') return !!vm.$can('approve_high_risk', 'RiskEvaluation')
        return !!(vm.$can('approve_low_risk', 'RiskEvaluation') || vm.$can('approve_high_risk', 'RiskEvaluation'))
      } catch (_) {
        return false
      }
    })

    const historyGridStyle = computed(() => {
      const base = 'max-content minmax(140px, 1fr) minmax(190px, max-content)'
      return {
        gridTemplateColumns: hasHistoryComments.value ? `${base} minmax(140px, 1fr)` : base,
      }
    })

    const humanize = (str = '') => String(str).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

    const labelForState = (name) => {
      const raw = String(name || '')
      const norm = raw.trim().toLowerCase().replace(/[-\s]+/g, '_')
      const aliases = { canceled: 'cancelled' }
      const canonical = aliases[norm] || norm
      const tryKeys = [`procedure_states.${canonical}`, `procedure_states.value.${canonical}`]
      for (const k of tryKeys) {
        const v = vm.$t ? vm.$t(k) : k
        if (typeof v === 'string' && v !== k) return v
      }
      return humanize(canonical)
    }

    const badgeFor = (name) => {
      const map = { in_progress: 'primary', in_review: 'warning', approved: 'success', rejected: 'danger', scheduled: 'info', canceled: 'canceled', reassigned: 'reassigned' }
      const key = String(name || '').toLowerCase()
      return map[key] || 'secondary'
    }

    const formatDate = (iso) => {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        return d.toLocaleString()
      } catch {
        return iso
      }
    }

    const getUserName = (s) => s?.user_name || s?.user?.name || ''
    const getUserEmail = (s) => {
      const u = s?.user
      if (!u) return ''
      if (typeof u === 'string') return u
      return u?.email || ''
    }
    const formatUser = (s) => {
      const name = getUserName(s)
      const email = getUserEmail(s)
      return [name, email].filter(Boolean).join(' | ')
    }
    const getStatusComment = (s) => s?.comment || s?.comments || s?.note || s?.notes || s?.description || ''

    const tf = (key, fallback = '') => {
      try {
        const out = vm.$t ? vm.$t(key) : key
        return typeof out === 'string' && out === key ? fallback : out
      } catch {
        return fallback
      }
    }

    const tc = (key, count = 1, fallback = '') => {
      try {
        const out = vm.$tc ? vm.$tc(key, count) : key
        return typeof out === 'string' && out === key ? fallback : out
      } catch {
        return fallback
      }
    }

    const resolveTemplateSourcesByIds = async (ids = []) => {
      if (!Array.isArray(ids) || !ids.length) return []
      try {
        const { data } = await axios.get('/v1/risk_templates')
        const all = data.data || []
        const set = new Set(ids.map(Number))
        return all.filter(t => set.has(Number(t.id))).map(t => ({ id: t.id, name: t.name }))
      } catch (err) {
        return []
      }
    }

    const loadEvaluationMeta = async (id) => {
      try {
        const { data } = await axios.get(`/v1/risk_evaluations/${id}`)
        const ev = data && data.data ? data.data : data
        referenceNumber.value = ev.reference_number || ''
        unitNameDisplay.value = ev.unit?.description || ''
        rems.value = ev.rems ?? ev.unit?.rems ?? ''
        sirh.value = ev.sirh ?? ev.unit?.sirh ?? ''
        currentState.value = ev.last_status?.name || 'in_progress'
        lastStatusUserId.value = ev.last_status?.id_user || ev.last_status?.user_id || ev.last_status?.user?.id || null
        statuses.value = Array.isArray(ev.statuses) ? ev.statuses : []

        const stLower = String(currentState.value || '').toLowerCase()
        let ownerId = ev.id_user || ev.user_id || ev.user?.id || null

        const list = Array.isArray(ev.statuses) ? ev.statuses : []

        if (!ownerId && list.length) {
          const reversed = list.slice().reverse()
          const ownerStatus = reversed.find(s => {
            const n = String(s?.name || '').toLowerCase()
            return n === 'reassigned' || n === 'in_progress'
          })
          if (ownerStatus) {
            ownerId = ownerStatus.id_user || ownerStatus.user_id || ownerStatus.reviewer_id || ownerStatus.id_reviewer || ownerStatus.user?.id || ownerStatus.userId || null
          }
        }

        if (!ownerId && (stLower === 'in_progress' || stLower === 'reassigned')) {
          ownerId = lastStatusUserId.value || null
        }

        ownerUserId.value = ownerId || null

        if (String(currentState.value).toLowerCase() === 'rejected') {
          loadReassignableOptions()
        } else {
          selectedReassign.value = null
        }

        if (ev.formAnswer?.template_sources?.length) {
          templateNames.value = ev.formAnswer.template_sources.map(s => (s && s.name ? s.name : null)).filter(Boolean).join(', ')
        } else if (inlineSources.value?.length) {
          const srcObjs = await resolveTemplateSourcesByIds(inlineSources.value)
          templateNames.value = srcObjs.map(s => s.name).join(', ')
        } else {
          templateNames.value = ''
        }

        evaluationFinal.value = {
          flagged_comments: ev.flagged_comments ?? '',
          grade_reviwed_residual_risk: ev.grade_reviwed_residual_risk ?? null,
          executive_resume: ev.executive_resume ?? '',
        }
      } catch (e) {
      }
    }

    const ensureAnswerExists = async () => {
      if (!currentEvaluationId.value) return
      if (didBootstrap.value || creating.value) return
      try {
        await axios.get(`/v1/risk_form_answers/get-form-by-evaluation/${currentEvaluationId.value}`)
        didBootstrap.value = true
      } catch (e) {
        creating.value = true
        try {
          const formData = new FormData()
          formData.append('id_risk_evaluation', currentEvaluationId.value)
          formData.append('data', JSON.stringify({}))
          formData.append('template_snapshot', JSON.stringify(inlineSnapshot.value || []))
          let sourcesToSend = []
          if (inlineSources.value && inlineSources.value.length) {
            sourcesToSend = await resolveTemplateSourcesByIds(inlineSources.value)
          }
          formData.append('template_sources', JSON.stringify(sourcesToSend))
          await axios.post(`v1/risk_form_answers`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
          templateNames.value = sourcesToSend.length ? sourcesToSend.map(s => s.name).join(', ') : inlineSnapshot.value?.length ? tf('risk.template.composed', 'Composed') : ''
          didBootstrap.value = true
          await loadEvaluationMeta(currentEvaluationId.value)
        } catch (err) {
          vm.$toast?.error?.(tf('custom_forms.cannot_start_form', 'No se pudo iniciar el formulario'))
        } finally {
          creating.value = false
        }
      }
    }

    const fetchEvaluation = async () => {
      try {
        const idUnit = props.data?.unit_id || props.data?.unit?.id
        const idEval = props.data?.evaluation_id || props.data?.id

        if (idEval) {
          currentEvaluationId.value = idEval
          await loadEvaluationMeta(idEval)
          await ensureAnswerExists()
          readyToRenderForm.value = true
        } else if (idUnit) {
          const params = { params: { id_unit: idUnit } }
          const response = await axios.get('/v1/risk_evaluations', params)
          const list = response.data.data || []
          if (list.length) {
            currentEvaluationId.value = list[0].id
            await loadEvaluationMeta(currentEvaluationId.value)
            await ensureAnswerExists()
            readyToRenderForm.value = true
          }
        }
      } catch (_) {
      } finally {
        loading.value = false
      }
    }

    const loadReviewerOptions = async () => {
      try {
        const { data } = await axios.get('/v1/risk_evaluations/getReviewers')
        const list = data?.data || []
        userOptions.value = list.map(u => ({ value: u.id, label: `${u.name}| ${u.email}` }))
      } catch (e) {
        userOptions.value = []
      }
    }

    const loadReassignableOptions = async () => {
      try {
        const { data } = await axios.get('/v1/risk_evaluations/getReassignable')
        const list = data?.data || []
        reassignableOptions.value = list.map(u => ({ value: u.id, label: (u.name || '') + (u.email ? ' | ' + u.email : '') }))
      } catch {
        reassignableOptions.value = []
      }
    }

    const _normalizeValidationResult = (res, fallbackMsg = 'Validación fallida') => {
      if (typeof res === 'boolean') return { ok: !!res, message: res ? '' : fallbackMsg }
      if (res && typeof res === 'object') {
        const ok = [res.ok, res.valid, res.success].find(v => typeof v === 'boolean')
        const msg = res.message || res.error || (Array.isArray(res.errors) ? res.errors.join(', ') : '')
        return { ok: ok === undefined ? true : ok, message: msg || fallbackMsg }
      }
      return { ok: true, message: '' }
    }

    const updateEvaluationStatus = async (newStatus, toUserId = null) => {
      if (!currentEvaluationId.value) return

      if (String(newStatus).toLowerCase() === 'in_review') {
        if (vm.$refs?.runner?.validateBeforeReview) {
          const vr = _normalizeValidationResult(await vm.$refs.runner.validateBeforeReview(), tf('risk.form_required', 'Faltan campos obligatorios'))
          if (!vr.ok) {
            vm.$toast?.error?.(vr.message)
            return
          }
        }
        if (vm.$refs?.threats?.validateBeforeReview) {
          const vt = _normalizeValidationResult(await vm.$refs.threats.validateBeforeReview(), tf('risk.threats_missing', 'Faltan pesos o respuestas en Amenazas'))
          if (!vt.ok) {
            vm.$toast?.error?.(vt.message)
            return
          }
        } else if (vm.$refs?.threats?.validateBeforeSave) {
          const vt = _normalizeValidationResult(await vm.$refs.threats.validateBeforeSave(), tf('risk.threats_missing', 'Faltan pesos o respuestas en Amenazas'))
          if (!vt.ok) {
            vm.$toast?.error?.(vt.message)
            return
          }
        }
        if (vm.$refs?.flagged?.validateBeforeReview) {
          const vn = _normalizeValidationResult(await vm.$refs.flagged.validateBeforeReview(), tf('risk.nona_missing', 'Faltan respuestas NO/NA'))
          if (!vn.ok) {
            vm.$toast?.error?.(vn.message)
            return
          }
        } else if (vm.$refs?.flagged?.validateBeforeSave) {
          const vn = _normalizeValidationResult(await vm.$refs.flagged.validateBeforeSave(), tf('risk.nona_missing', 'Faltan respuestas NO/NA'))
          if (!vn.ok) {
            vm.$toast?.error?.(vn.message)
            return
          }
        }

        try {
          if (vm.$refs?.flagged?.saveAll) await vm.$refs.flagged.saveAll()
        } catch (_) {}
        try {
          if (vm.$refs?.threats?.saveAll) await vm.$refs.threats.saveAll()
        } catch (_) {}
        await _saveRunnerStrict()
      }

      const guarded = ['approved', 'rejected']
      if (guarded.includes(String(newStatus)) && !isReviewer.value) return

      try {
        loading.value = true
        const payload = { status: newStatus }
        if (toUserId) payload.id_user = toUserId
        await axios.post(`/v1/risk_evaluations/update_status/${currentEvaluationId.value}`, payload)
        currentState.value = newStatus
        vm.$toast?.success?.(`${tf('custom_forms.state', 'Estado')}: ${labelForState(newStatus.toLowerCase())}`)
        await loadEvaluationMeta(currentEvaluationId.value)
        vm.$nextTick(() => vm.$refs.runner?.reloadForm?.())
      } catch (err) {
        vm.$toast?.error?.(tf('custom_forms.update_state_error', 'No se pudo actualizar el estado'))
      } finally {
        loading.value = false
      }
    }

    const _saveRunnerStrict = async () => {
      const r = vm.$refs?.runner
      if (!r) return false

      try {
        if (typeof r.validateBeforeSave === 'function') {
          const res = await r.validateBeforeSave()
          if (res === false || (res && res.ok === false)) {
            vm.$toast?.error?.(tf('risk.form_required', 'Faltan campos obligatorios'))
            return false
          }
        }
      } catch (_) {}

      const tryCall = async (name) => {
        try {
          if (typeof r[name] === 'function') {
            await r[name]()
            return true
          }
        } catch (e) {
        }
        return false
      }

      if (await tryCall('saveAll')) return true
      if (await tryCall('save')) return true
      if (await tryCall('submitForm')) return true
      if (await tryCall('submit')) return true

      if (r.$refs?.form?.submit) {
        r.$refs.form.submit()
        await new Promise(res => setTimeout(res, 400))
        return true
      }

      if (typeof r.$emit === 'function') {
        r.$emit('submit')
        await new Promise(res => setTimeout(res, 400))
        return true
      }

      return false
    }

    const onChildDirty = (reason = 'child') => {
      scheduleAutosave(reason)
    }

    const scheduleAutosave = (reason = 'unknown') => {
      if (!asEnabled.value || loading.value || saving.value) return
      clearTimeout(asDebounce.value)
      asDebounce.value = setTimeout(() => _doAutosave(`debounce:${reason}`), asDebounceMs.value)
      if (!asMax.value) {
        asMax.value = setTimeout(() => _doAutosave(`max:${reason}`), asMaxIntervalMs.value)
      }
    }

    const _clearAutosaveTimers = () => {
      clearTimeout(asDebounce.value)
      asDebounce.value = null
      clearTimeout(asMax.value)
      asMax.value = null
    }

    const _doAutosave = async () => {
      if (!asEnabled.value || asBusy.value || loading.value) {
        asQueued.value = true
        return
      }
      asBusy.value = true
      try {
        await _saveThreatsAllowPartial(true)
        await _saveFormSilent()
        await _sleep(250)
        try {
          await vm.$refs.flagged?.refreshIfChanged?.({ mode: 'soft' })
        } catch {}
        const openBefore = _getFlaggedOpenIds()
        if (vm.$refs?.flagged?.saveAll) {
          try {
            await _withPartialSave(async () => {
              await vm.$refs.flagged.saveAll()
            }, 'flagged')
          } catch (e) {
          }
          _restoreFlaggedOpenIds(openBefore)
        }
        await _saveFinalSilent()
      } finally {
        asBusy.value = false
        _clearAutosaveTimers()
        if (asQueued.value) {
          asQueued.value = false
          scheduleAutosave('queued')
        }
      }
    }

    const _getFlaggedOpenIds = () => {
      try {
        const F = vm.$refs?.flagged
        if (!F || !F.expanded) return []
        return Object.keys(F.expanded).filter(k => !!F.expanded[k]).map(k => Number(k))
      } catch {
        return []
      }
    }

    const _restoreFlaggedOpenIds = (openIds = []) => {
      try {
        const F = vm.$refs?.flagged
        if (!F) return
        const set = new Set((Array.isArray(F.flagged) ? F.flagged : []).map(r => Number(r.id)))
        const toOpen = openIds.filter(id => set.has(Number(id)))
        F.expanded = F.expanded || {}
        Object.keys(F.expanded).forEach(k => {
          F.$set(F.expanded, k, false)
        })
        toOpen.forEach(id => {
          F.$set(F.expanded, id, true)
        })
      } catch {}
    }

    const _saveFinalSilent = async () => {
      if (!currentEvaluationId.value) return
      try {
        const payload = {
          flagged_comments: evaluationFinal.value.flagged_comments || null,
          grade_reviwed_residual_risk: evaluationFinal.value.grade_reviwed_residual_risk ?? null,
          executive_resume: evaluationFinal.value.executive_resume || null,
        }
        await axios.put(`/v1/risk_evaluations/${currentEvaluationId.value}`, payload)
      } catch (e) {
      }
    }

    const _withPartialSave = async (fn, scope = 'both') => {
      const isThreats = scope === 'threats' || scope === 'both'
      const isFlagged = scope === 'flagged' || scope === 'both'
      const isForm = scope === 'form'

      const rxThreats = /(risk[_-]?evaluation[_-]?threat|risk[_-]?threat|\/threats?\/|\/threats$|\/weights|\/summary)/i
      const rxFlagged = /(flagged[_-]?answers?|\/flagged_answers?)/i
      const rxForm = /(risk[_-]?form[_-]?answers?|\/risk_form_answers|\/dynamic[_-]?forms?|\/custom[_-]?forms?|\/form_answers?)/i

      const reqId = axios.interceptors.request.use(cfg => {
        try {
          const url = String(cfg?.url || '')
          const matchThreats = isThreats && rxThreats.test(url)
          const matchFlagged = isFlagged && rxFlagged.test(url)
          const matchForm = isForm && rxForm.test(url)
          if (matchThreats || matchFlagged || matchForm) {
            cfg.params = { ...(cfg.params || {}), partial: 1 }
            cfg.headers = {
              ...(cfg.headers || {}),
              'X-Requested-From': 'autosave',
              ...(matchThreats ? { 'X-Skip-Weights-Validation': '1' } : {}),
              ...(matchFlagged ? { 'X-Skip-Empty-Validation': '1' } : {}),
              ...(matchForm ? { 'X-Skip-Required-Validation': '1' } : {}),
            }
          }
        } catch {}
        return cfg
      })

      try {
        return await fn()
      } finally {
        axios.interceptors.request.eject(reqId)
      }
    }

    const _saveThreatsAllowPartial = async (silent = false) => {
      if (!vm.$refs?.threats) return
      const cmp = vm.$refs.threats

      await _withPartialSave(async () => {
        let didSpecific = false

        try {
          if (typeof cmp.saveWeights === 'function') {
            await cmp.saveWeights({ allowPartial: true, silent: !!silent })
            didSpecific = true
          }
        } catch (e) {
        }

        try {
          if (typeof cmp.saveAnswersOnly === 'function') {
            await cmp.saveAnswersOnly({
              allowPartial: true,
              skipWeightValidation: true,
              skipEmptyValidation: true,
              silent: !!silent,
            })
            didSpecific = true
          } else if (typeof cmp.saveSectionsOnly === 'function') {
            await cmp.saveSectionsOnly({
              allowPartial: true,
              skipWeightValidation: true,
              skipEmptyValidation: true,
              silent: !!silent,
            })
            didSpecific = true
          }
        } catch (e) {
        }

        if (!didSpecific && typeof cmp.saveAll === 'function') {
          try {
            await cmp.saveAll()
          } catch (e) {
          }
        }

        try {
          if (typeof cmp.saveSummary === 'function') {
            await cmp.saveSummary({ allowPartial: true, silent: !!silent })
          }
        } catch (e) {
        }
      }, 'threats')
    }

    const _saveFormSilent = async () => {
      if (!vm.$refs?.runner) return
      await _withPartialSave(async () => {
        const r = vm.$refs.runner
        if (typeof r.saveDraft === 'function') {
          await r.saveDraft({ silent: true })
        } else if (typeof r.submitForm === 'function') {
          await r.submitForm({ silent: true, partial: true })
        } else if (r.$refs?.form?.submit) {
          r.$refs.form.submit()
          await new Promise(res => setTimeout(res, 350))
        }
      }, 'form')
    }

    const _sleep = (ms = 250) => new Promise(res => setTimeout(res, ms))

    const fetchInherentLevel = async () => {
      try {
        const { data } = await axios.get(`/v1/risk_evaluations/${currentEvaluationId.value}/threats/summary`)
        const payload = data?.data || data || {}
        const val = Number(payload?.weighted?.inherent ?? payload?.inherent ?? null)
        if (!Number.isFinite(val)) return null
        let lvl = Math.round(val)
        if (lvl < 1) lvl = 1
        if (lvl > 5) lvl = 5
        return lvl
      } catch {
        return null
      }
    }

    const computeApprovalPermission = async () => {
      const reviewed = evaluationFinal.value?.grade_reviwed_residual_risk
      let level = reviewed != null ? Number(reviewed) : null
      if (!(Number.isFinite(level) && level >= 1 && level <= 5)) {
        level = await fetchInherentLevel()
      }
      inherentLevel.value = Number.isFinite(level) ? level : null
      if (inherentLevel.value == null) {
        approvePermNeeded.value = null
        return
      }
      approvePermNeeded.value = inherentLevel.value <= 3 ? 'approve_low_risk' : 'approve_high_risk'
    }

    const onClickSave = async () => {
      if (!currentEvaluationId.value) return
      try {
        saving.value = true
        await _saveThreatsAllowPartial(false)
        await _saveFormSilent()
        await _sleep(250)
        try {
          await vm.$refs.flagged?.refreshIfChanged?.({ mode: 'soft' })
        } catch {}
        const openBefore = _getFlaggedOpenIds()
        if (vm.$refs?.flagged?.saveAll) {
          await _withPartialSave(async () => {
            await vm.$refs.flagged.saveAll()
          }, 'flagged')
          _restoreFlaggedOpenIds(openBefore)
        }
        try {
          const payload = {
            flagged_comments: evaluationFinal.value.flagged_comments || null,
            grade_reviwed_residual_risk: evaluationFinal.value.grade_reviwed_residual_risk ?? null,
            executive_resume: evaluationFinal.value.executive_resume || null,
          }
          await axios.put(`/v1/risk_evaluations/${currentEvaluationId.value}`, payload)
        } catch {
          vm.$toast?.warning?.(tf('risk.final_part_save_warning', 'No se pudo guardar la sección final'))
        }

        const msg = tf('custom_forms.answers_saved', 'Respuestas guardadas')
        vm.$toast && vm.$toast({
          component: ToastificationContent,
          props: { title: msg, icon: 'CheckIcon', variant: 'success' },
        })

        await computeApprovalPermission()
      } catch {
        vm.$toast?.error?.(tf('risk.save_failed', 'No se pudo guardar'))
      } finally {
        saving.value = false
      }
    }

    const handleWizardOpen = async ({ id, snapshot, sources }) => {
      vm.$root.$emit('bv::hide::modal', 'risk-evaluation-wizard')
      currentEvaluationId.value = id
      inlineSnapshot.value = Array.isArray(snapshot) ? snapshot : null
      inlineSources.value = Array.isArray(sources) ? sources : null
      runnerKey.value += 1
      loading.value = true
      try {
        await loadEvaluationMeta(id)
        await ensureAnswerExists()
        readyToRenderForm.value = true
      } finally {
        loading.value = false
      }
    }

    const onAnswersSubmit = () => {}

    watch(evaluationFinal, () => {
      scheduleAutosave('final:changed')
      computeApprovalPermission().catch(() => {})
    }, { deep: true })

    onMounted(async () => {
      await Promise.all([fetchEvaluation(), loadReviewerOptions(), loadReassignableOptions()])
      if (currentEvaluationId.value) {
        await computeApprovalPermission()
      }
    })

    onBeforeUnmount(() => {
      _clearAutosaveTimers()
    })

    return {
      loading, saving, evaluation, currentEvaluationId, inlineSnapshot, inlineSources,
      referenceNumber, unitNameDisplay, rems, sirh, currentState, templateNames, statuses,
      readyToRenderForm, runnerKey, showAllHistory, limitHistory, selectedReviewer, selectedReassign,
      evaluationFinal, isReviewer, canSendToReview, canReassignAfterRejected, canShowReassign,
      userOptions, reassignableOptions, sortedHistory, visibleHistory, hasHistoryComments,
      historyGridStyle, showSaveButton, showApproveButtons, labelForState, badgeFor, formatDate,
      formatUser, getUserName, getUserEmail, getStatusComment, updateEvaluationStatus,
      onAnswersSubmit, onClickSave, handleWizardOpen, tf, tc, onChildDirty,
    }
  },
}
</script>

<style lang="scss" scoped>
.risk-evaluation-slot {
  width: 100%;
  height: 100%;
}

.info-card {
  background: #f9fafb;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 10px 12px;
}
.info-row {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}
.info-label {
  font-weight: 600;
  color: #444;
}
.info-value {
  color: #111;
}

.history-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  overflow: hidden;
}
.history-header {
  margin-bottom: 8px;
}
.history-table {
  width: 100%;
}
.history-row {
  display: grid;
  column-gap: 8px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #eee;
}
.history-row:last-child {
  border-bottom: 0;
}
.history-row--head {
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #444;
  padding-top: 0;
  padding-bottom: 8px;
}
.col-state {
  justify-self: start;
}
.col-user {
  justify-self: start;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-date {
  justify-self: end;
  white-space: nowrap;
}
.col-note {
  white-space: normal;
  overflow: hidden;
}
.history-badge {
  display: inline-flex;
  align-items: center;
}
.history-date {
  font-size: 12px;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}
.history-user {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.history-user .user-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.history-user .user-email {
  font-size: 12px;
  color: #6c757d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reviewer-inline {
  flex: 0 1 560px;
  min-width: 220px;
  max-width: 380px;
}
</style>

<style>
.vs__dropdown-menu {
  z-index: 2055 !important;
}
.select-opens-up .vs__dropdown-menu {
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
}
</style>
